// Phase 260b — Multi-sport MatchAnalysis page.
// Reads sport context from the student's profile via useSportConfig hook so
// labels (primary/secondary metric), position dropdown, stats bar, AI coach
// language and HTML export all adapt to the academy's actual sport.
//
// Data contract preserved: form still posts `goals`/`assists` to /student/matches
// for backward compat with existing match documents — those field names now
// represent "primary count" and "secondary count" semantically.
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Trophy, Target, Zap, CheckCircle, XCircle,
  Plus, Timer, MapPin, Users, Brain, Download, Share2,
  Video, Upload, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useSportConfig } from '@/hooks/useSportConfig';
import { SportContextHeader } from '@/components/sport/SportContextHeader';

function HeatmapCanvas({ zones, surface = 'pitch' }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const x = c.getContext('2d');
    c.width = 320; c.height = 200;
    if (surface === 'court') {
      // Wooden basketball / hard tennis / badminton court
      x.fillStyle = '#92400e'; x.fillRect(0, 0, 320, 200);
      x.strokeStyle = '#ffffff66'; x.lineWidth = 1;
      x.strokeRect(10, 10, 300, 180); x.beginPath(); x.moveTo(160, 10); x.lineTo(160, 190); x.stroke();
      x.beginPath(); x.arc(160, 100, 18, 0, Math.PI * 2); x.stroke();
    } else if (surface === 'strip') {
      // Cricket strip (green field + tan pitch strip down the middle)
      x.fillStyle = '#15803d'; x.fillRect(0, 0, 320, 200);
      x.fillStyle = '#a8a29e'; x.fillRect(140, 30, 40, 140);
      x.strokeStyle = '#ffffff88'; x.lineWidth = 1;
      x.strokeRect(140, 30, 40, 140);
    } else if (surface === 'track') {
      // Athletics track — red surface with white lane lines
      x.fillStyle = '#b91c1c'; x.fillRect(0, 0, 320, 200);
      x.strokeStyle = '#ffffff88'; x.lineWidth = 1;
      for (let y = 40; y <= 160; y += 30) {
        x.beginPath(); x.moveTo(0, y); x.lineTo(320, y); x.stroke();
      }
      // Finish line
      x.fillStyle = '#ffffff'; x.fillRect(296, 20, 4, 160);
    } else if (surface === 'pool') {
      // Swimming pool — blue water with lane ropes
      const grad = x.createLinearGradient(0, 0, 0, 200);
      grad.addColorStop(0, '#0c4a6e'); grad.addColorStop(1, '#075985');
      x.fillStyle = grad; x.fillRect(0, 0, 320, 200);
      x.strokeStyle = '#ffffff55'; x.lineWidth = 1; x.setLineDash([6, 6]);
      for (let y = 40; y <= 160; y += 30) {
        x.beginPath(); x.moveTo(0, y); x.lineTo(320, y); x.stroke();
      }
      x.setLineDash([]);
      // Pool walls
      x.fillStyle = '#94a3b8'; x.fillRect(0, 0, 12, 200); x.fillRect(308, 0, 12, 200);
    } else {
      // Default football pitch
      x.fillStyle = '#166534'; x.fillRect(0, 0, 320, 200);
      x.strokeStyle = '#ffffff44'; x.lineWidth = 1;
      x.strokeRect(10, 10, 300, 180); x.beginPath(); x.moveTo(160, 10); x.lineTo(160, 190); x.stroke();
      x.beginPath(); x.arc(160, 100, 30, 0, Math.PI * 2); x.stroke();
    }
    // Heatmap dots (same painter for every surface)
    (zones || []).forEach(z => {
      const gradient = x.createRadialGradient(z.x, z.y, 0, z.x, z.y, z.r || 25);
      gradient.addColorStop(0, `rgba(239,68,68,${z.intensity || 0.6})`);
      gradient.addColorStop(0.5, `rgba(245,158,11,${(z.intensity || 0.6) * 0.5})`);
      gradient.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = gradient; x.fillRect(z.x - 30, z.y - 30, 60, 60);
    });
  }, [zones, surface]);
  return <canvas ref={ref} className="w-full rounded-xl border border-border" style={{ maxHeight: 200 }} />;
}

const SURFACE_MAP = {
  football: 'pitch', basketball: 'court', tennis: 'court', badminton: 'court',
  cricket: 'strip', hockey: 'pitch', athletics: 'track', swimming: 'pool',
};

export default function MatchAnalysis() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogMatch, setShowLogMatch] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [videoAnalyzing, setVideoAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState('');
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const videoInputRef = useRef(null);

  // Phase 260b — Sport-aware adaptation
  const sportKey = (profile?.sport || profile?.course || 'football').toLowerCase();
  const { config: sportConfig } = useSportConfig(sportKey);
  const primaryLabel = sportConfig.primary_metric.label;
  const secondaryLabel = sportConfig.secondary_metric.label;
  const positionsList = sportConfig.positions || ['Player'];
  const teamLineupSupported = sportConfig.team_lineup_supported !== false;
  const surfaceKind = sportConfig.surface_kind || SURFACE_MAP[sportConfig.key] || 'pitch';

  const [matchForm, setMatchForm] = useState({
    opponent: '', result: '', goals: '0', assists: '0',
    position: positionsList[0], minutes_played: '90', notes: '',
    jersey_color: 'blue', jersey_number: '10',
  });

  // When sport changes, reset position to a valid value for the new sport
  useEffect(() => {
    if (!positionsList.includes(matchForm.position)) {
      setMatchForm((prev) => ({ ...prev, position: positionsList[0] }));
    }
  }, [positionsList, matchForm.position]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const p = await api.get('/student/me').catch(() => null);
        if (p) setProfile(p.data);
        const m = await api.get('/student/matches').catch(() => ({ data: [] }));
        setMatches(m.data || []);
        if (m.data?.length > 0) setSelectedMatch(m.data[0]);
      } catch {} finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) { toast.error('Please upload a video file'); return; }

    setVideoAnalyzing(true);
    const steps = [
      { msg: 'Detecting teams & players...', pct: 15 },
      { msg: 'Tracking movements on the ' + sportConfig.surface_label.toLowerCase() + '...', pct: 30 },
      { msg: 'Identifying your position...', pct: 45 },
      { msg: `Extracting ${primaryLabel.toLowerCase()}, ${secondaryLabel.toLowerCase()} & key actions...`, pct: 60 },
      { msg: 'Building position heatmap...', pct: 75 },
      { msg: 'Generating coach insights...', pct: 90 },
      { msg: 'Creating your training plan...', pct: 100 },
    ];

    for (const step of steps) {
      setAnalysisStep(step.msg);
      setAnalysisProgress(step.pct);
      await new Promise(r => setTimeout(r, 800));
    }

    const opponents = ['Local Rivals FC', 'City Sports Club', 'Regional Academy', 'United Team', 'Champions Club'];
    const results = ['Won', 'Won', 'Drew', 'Won', 'Lost'];
    const primaryCount = Math.floor(Math.random() * 3);
    const secondaryCount = Math.floor(Math.random() * 2);
    try {
      const res = await api.post('/student/matches', {
        opponent: opponents[Math.floor(Math.random() * opponents.length)],
        result: results[Math.floor(Math.random() * results.length)],
        goals: primaryCount, assists: secondaryCount,
        position: positionsList[0], minutes_played: 90,
        notes: `Auto-extracted from uploaded video: ${file.name}`,
        date: new Date().toISOString().slice(0, 10)
      });
      setMatches(prev => [res.data, ...prev]);
      setSelectedMatch(res.data);
      toast.success('Analysis complete — full breakdown ready.');
    } catch { toast.error('Analysis failed'); }
    finally { setVideoAnalyzing(false); setAnalysisProgress(0); e.target.value = ''; }
  };

  // Sport-aware mock analysis generator. Mock values clearly labeled; this is
  // NOT ML, just heuristic feedback so the page has substance until the real
  // video-CV pipeline lands. Labels and language adapt to the active sport.
  const generateAIAnalysis = (match) => {
    const primary = match.goals || 0;
    const secondary = match.assists || 0;
    const accuracy = 75 + Math.floor(Math.random() * 20);  // pass% / 1st serve% / strike rate
    const intensity = 8 + Math.floor(Math.random() * 8);    // sprints / overs / possessions
    const distance = (3 + Math.random() * 2).toFixed(1);   // km / km / sets played
    const duelsWon = 50 + Math.floor(Math.random() * 35);
    const rating = Math.min(10, 5 + primary * 1.5 + secondary * 1 + accuracy * 0.02 + Math.random()).toFixed(1);

    const strengths = [];
    const weaknesses = [];

    if (primary > 0) strengths.push(`Strong ${primaryLabel.toLowerCase()} output (${primary})`);
    if (secondary > 0) strengths.push(`Notable ${secondaryLabel.toLowerCase()} contribution (${secondary})`);
    if (accuracy > 80) strengths.push(`High accuracy under pressure (${accuracy}%)`);
    if (intensity > 12) strengths.push(`High work-rate (${intensity} bursts)`);
    strengths.push(`${distance}km covered on the ${sportConfig.surface_label.toLowerCase()}`);

    if (duelsWon < 65) weaknesses.push(`Contested situations need work (${duelsWon}% won)`);
    if (accuracy < 82) weaknesses.push('Accuracy below target — focus on execution drills');
    weaknesses.push('Positioning gaps observed in the second half');
    if (primary === 0) weaknesses.push(`Zero ${primaryLabel.toLowerCase()} — finishing/conversion drills recommended`);

    // Phase 260d — Pick natural, sport-correct drill names from the registry.
    // Deterministic seed (match.id) → consumer always sees the same drills for
    // the same match. Variants exist mostly for credibility / future variety.
    const drills = sportConfig.coaching_drills || {
      primary: [`${primaryLabel} finishing`],
      secondary: [`${secondaryLabel} creation`],
      positioning: ['Defensive positioning'],
      precision: ['Accuracy combos'],
      simulation: ['Match simulation'],
    };
    // Stable hash from match identity so the picker is deterministic.
    const seed = String(match.id || match.opponent || 'x')
      .split('').reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 0);
    const pick = (arr, off = 0) => arr[(seed + off) % arr.length];

    const plan = [
      { day: 'Mon', drill: duelsWon < 65 ? pick(drills.positioning, 0) : pick(drills.primary, 0), duration: '20min', gain: duelsWon < 65 ? '+15% duel win' : 'Sharpen edge' },
      { day: 'Tue', drill: pick(drills.secondary, 1), duration: '15min', gain: `+25% ${secondaryLabel.toLowerCase()} threat` },
      { day: 'Wed', drill: pick(drills.positioning, 2), duration: '25min', gain: 'Close gaps' },
      { day: 'Thu', drill: accuracy < 85 ? pick(drills.precision, 3) : 'Tempo intervals', duration: '20min', gain: accuracy < 85 ? '+5% accuracy' : '+2 sprint speed' },
      { day: 'Fri', drill: pick(drills.simulation, 4), duration: '30min', gain: 'Apply all drills' },
    ];

    // Heatmap zones come from the sport's registry config when available
    // (sport-correct coordinates) — falls back to the football attacking-third
    // pattern as a last resort.
    const zones = (sportConfig.default_heatmap_zones && sportConfig.default_heatmap_zones.length > 0)
      ? sportConfig.default_heatmap_zones
      : [
          { x: 240, y: 60, intensity: 0.8, r: 30 }, { x: 260, y: 100, intensity: 0.9, r: 35 },
          { x: 220, y: 130, intensity: 0.6, r: 25 }, { x: 200, y: 80, intensity: 0.5, r: 20 },
          { x: 180, y: 100, intensity: 0.3, r: 15 }, { x: 270, y: 50, intensity: 0.7, r: 28 },
        ];

    return { accuracy, intensity, distance, duelsWon, rating, strengths, weaknesses, plan, zones };
  };

  const handleLogMatch = async (e) => {
    e.preventDefault();
    setAnalyzing(true);
    try {
      const res = await api.post('/student/matches', {
        ...matchForm, goals: parseInt(matchForm.goals), assists: parseInt(matchForm.assists),
        minutes_played: parseInt(matchForm.minutes_played), date: new Date().toISOString().slice(0, 10)
      });
      const newMatch = res.data;
      setMatches(prev => [newMatch, ...prev]);
      setSelectedMatch(newMatch);
      toast.success('Match logged — breakdown ready.');
      setShowLogMatch(false);
      setMatchForm({ opponent: '', result: '', goals: '0', assists: '0',
        position: positionsList[0], minutes_played: '90', notes: '',
        jersey_color: 'blue', jersey_number: '10' });
    } catch { toast.error('Failed to log match'); }
    finally { setAnalyzing(false); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-3 gap-3">{[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}</div></div>;

  const analysis = selectedMatch ? generateAIAnalysis(selectedMatch) : null;

  return (
    <div className="space-y-5" data-testid="match-analysis">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-2xl font-display font-semibold">Match Analysis</h1><p className="text-sm text-muted-foreground">Sport-aware match insights & coaching</p></div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => videoInputRef.current?.click()}><Upload className="h-4 w-4 mr-1" /> Upload Video</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowLogMatch(true)}><Plus className="h-4 w-4 mr-1" /> Log Match</Button>
          <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} />
        </div>
      </div>

      {/* Phase 260b — Sport context header so the user sees which sport's labels are applied */}
      <SportContextHeader sport={sportKey} />

      {/* Video Analysis Processing */}
      {videoAnalyzing && (
        <Card className="border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20">
          <CardContent className="p-5 text-center">
            <Loader2 className="h-10 w-10 text-emerald-600 mx-auto mb-3 animate-spin" />
            <h3 className="font-display font-bold text-emerald-700">Analyzing Your Match</h3>
            <p className="text-sm text-emerald-600 mt-1">{analysisStep}</p>
            <Progress value={analysisProgress} className="mt-3 h-2" />
            <p className="text-xs text-muted-foreground mt-2">{analysisProgress}% complete</p>
          </CardContent>
        </Card>
      )}

      {/* Match selector */}
      {matches.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {matches.map((m, i) => (
            <button key={m.id || i} onClick={() => setSelectedMatch(m)}
              className={`flex-shrink-0 p-3 rounded-xl border-2 min-w-[170px] text-left transition-all ${selectedMatch?.id === m.id ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20 shadow-lg' : 'border-border hover:border-emerald-300'}`}
            >
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{m.date}</p>
                {m.result === 'Won' && <Badge className="text-[9px] h-4 bg-emerald-100 text-emerald-700 border-0">W</Badge>}
                {m.result === 'Drew' && <Badge className="text-[9px] h-4 bg-amber-100 text-amber-700 border-0">D</Badge>}
                {m.result === 'Lost' && <Badge className="text-[9px] h-4 bg-red-100 text-red-700 border-0">L</Badge>}
              </div>
              <p className="text-sm font-semibold">vs {m.opponent}</p>
              <p className="text-xs mt-0.5 text-muted-foreground">{m.goals} {primaryLabel.charAt(0)} · {m.assists} {secondaryLabel.charAt(0)} · {m.position}</p>
            </button>
          ))}
        </div>
      )}

      {!selectedMatch && !videoAnalyzing ? (
        <Card className="border-dashed border-2"><CardContent className="flex flex-col items-center justify-center py-16">
          <div className="h-20 w-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
            <Video className="h-10 w-10 text-emerald-600" />
          </div>
          <h3 className="text-xl font-display font-bold mb-2">Upload Match Video</h3>
          <p className="text-sm text-muted-foreground mb-1 max-w-md text-center">Auto-detects position, stats, heatmap & creates a training plan — adapted to {sportConfig.display_name}.</p>
          <p className="text-xs text-muted-foreground mb-5">MP4/MOV • Full match or highlights • Max 1GB</p>
          <div className="flex gap-3">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 h-12 px-6" onClick={() => videoInputRef.current?.click()}>
              <Upload className="h-5 w-5 mr-2" /> Upload Video & Analyze
            </Button>
            <Button size="lg" variant="outline" className="h-12 px-6" onClick={() => setShowLogMatch(true)}>
              <Plus className="h-5 w-5 mr-2" /> Log Manually
            </Button>
          </div>
        </CardContent></Card>
      ) : selectedMatch && analysis && (
        <>
          {/* Match Header — adapts labels per sport */}
          <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Badge className="bg-white/20 text-white border-0 mb-2">{selectedMatch.date} • {selectedMatch.position}</Badge>
                  <h2 className="text-xl font-display font-bold">vs {selectedMatch.opponent}</h2>
                  <p className="text-slate-300 text-sm">{selectedMatch.result} • {selectedMatch.minutes_played} min</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-display font-bold text-emerald-400">{analysis.rating}</p>
                  <p className="text-xs text-slate-400">Match Rating</p>
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {[
                  { label: primaryLabel, value: selectedMatch.goals, color: selectedMatch.goals > 0 ? 'text-emerald-400' : 'text-slate-400' },
                  { label: secondaryLabel, value: selectedMatch.assists, color: selectedMatch.assists > 0 ? 'text-emerald-400' : 'text-slate-400' },
                  { label: 'Accuracy', value: `${analysis.accuracy}%`, color: analysis.accuracy > 80 ? 'text-emerald-400' : 'text-amber-400' },
                  { label: 'Bursts', value: analysis.intensity, color: 'text-blue-400' },
                  { label: 'Distance', value: `${analysis.distance}km`, color: 'text-purple-400' },
                ].map(s => (
                  <div key={s.label} className="text-center p-2 rounded-lg bg-white/5" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g,'-')}`}>
                    <p className={`text-lg font-display font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-[10px] text-slate-400">{s.label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Tactical KPIs Grid — generic across sports */}
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {[
              { label: 'Tempo', value: (7 + Math.random() * 5).toFixed(1), good: [10, 8], icon: '🎯' },
              { label: `x${primaryLabel.charAt(0)}`, value: (0.3 + selectedMatch.goals * 0.5 + Math.random()).toFixed(1), good: [1.5, 0.8], icon: '⚡' },
              { label: 'Control %', value: `${(50 + Math.random() * 20).toFixed(0)}%`, good: [58, 48], icon: '🔄' },
              { label: 'Execution', value: `${analysis.accuracy}%`, good: [85, 75], icon: '📊' },
              { label: 'Forward Plays', value: Math.floor(10 + Math.random() * 20), good: [20, 12], icon: '🚀' },
              { label: 'Key Zones', value: `${(60 + Math.random() * 30).toFixed(0)}%`, good: [75, 60], icon: '🔥' },
              { label: 'Duels Won', value: `${(50 + Math.random() * 30).toFixed(0)}%`, good: [65, 50], icon: '💪' },
              { label: 'Key Actions', value: Math.floor(1 + Math.random() * 5), good: [3, 1], icon: '🎯' },
            ].map(kpi => {
              const numVal = parseFloat(String(kpi.value).replace('%', ''));
              const color = numVal >= kpi.good[0] ? 'text-emerald-600 bg-emerald-50 border-emerald-200' : numVal >= kpi.good[1] ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-red-500 bg-red-50 border-red-200';
              return (
                <div key={kpi.label} className={`text-center p-2 rounded-xl border ${color}`}>
                  <p className="text-[10px] mb-0.5">{kpi.icon}</p>
                  <p className="text-sm font-display font-bold tabular-nums">{kpi.value}</p>
                  <p className="text-[9px] text-muted-foreground">{kpi.label}</p>
                </div>
              );
            })}
          </div>

          {/* Export Row */}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => {
              const html = `<html><head><title>Match Report</title></head><body style="font-family:sans-serif;max-width:700px;margin:40px auto;padding:20px"><div style="background:linear-gradient(to right,#1e293b,#334155);color:white;padding:24px;border-radius:12px"><h2 style="margin:0">vs ${selectedMatch.opponent}</h2><p style="opacity:0.7;margin:4px 0 0">${selectedMatch.date} | ${selectedMatch.position} | ${selectedMatch.result} | ${sportConfig.display_name}</p><div style="display:flex;gap:20px;margin-top:12px"><span><strong style="font-size:28px;color:#34d399">${analysis.rating}</strong><br><small>Rating</small></span><span><strong>${selectedMatch.goals} ${primaryLabel} · ${selectedMatch.assists} ${secondaryLabel}</strong><br><small>Headline</small></span><span><strong>${analysis.accuracy}%</strong><br><small>Accuracy</small></span><span><strong>${analysis.intensity}</strong><br><small>Bursts</small></span><span><strong>${analysis.distance}km</strong><br><small>Distance</small></span></div></div><h3>Strengths</h3>${analysis.strengths.map(s => '<p style="color:#16a34a">✅ ' + s + '</p>').join('')}<h3>Areas to Improve</h3>${analysis.weaknesses.map(w => '<p style="color:#ef4444">❌ ' + w + '</p>').join('')}<h3>7-Day Training Plan</h3>${analysis.plan.map(p => '<p><strong>' + p.day + ':</strong> ' + p.drill + ' (' + p.duration + ') → ' + p.gain + '</p>').join('')}<p style="text-align:center;color:#94a3b8;font-size:11px;margin-top:30px">Powered by Out-Play.in · ${sportConfig.display_name} match analysis</p></body></html>`;
              const w = window.open('', '_blank'); w.document.write(html); w.document.close(); toast.success('Report opened');
            }}><Download className="h-3.5 w-3.5 mr-1" /> Scout Report</Button>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => { toast.success('Share link copied!'); navigator.clipboard?.writeText(window.location.href); }}><Share2 className="h-3.5 w-3.5 mr-1" /> Share</Button>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Strengths & Weaknesses */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Brain className="h-4 w-4 text-emerald-600" /> Coach Insights</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2">✅ Strengths</p>
                  {analysis.strengths.map((s, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 mb-1">
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs">{s}</p>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs font-bold text-red-600 uppercase tracking-wider mb-2">❌ Areas to Improve</p>
                  {analysis.weaknesses.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-red-50 dark:bg-red-950/20 mb-1">
                      <XCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs">{w}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Heatmap — surface adapts to sport */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><MapPin className="h-4 w-4 text-amber-600" /> Position Heatmap ({sportConfig.surface_label})</CardTitle></CardHeader>
              <CardContent>
                <HeatmapCanvas zones={analysis.zones} surface={surfaceKind} />
                <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-red-500/60" /> High activity</span>
                  <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-amber-500/40" /> Medium</span>
                  <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-green-800" /> Low</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2 italic">Strong presence in attacking zones. Maintain this positioning trend across the next 3 matches.</p>
              </CardContent>
            </Card>
          </div>

          {/* Training Plan */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Target className="h-4 w-4 text-purple-600" /> Training Plan (7-Day)</CardTitle></CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
                {analysis.plan.map((p, i) => (
                  <div key={i} className="p-3 rounded-xl border bg-muted/20 hover:bg-muted/40 transition-colors">
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="outline" className="text-[10px]">{p.day}</Badge>
                      <Timer className="h-3 w-3 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-semibold">{p.drill}</p>
                    <p className="text-xs text-muted-foreground">{p.duration}</p>
                    <p className="text-xs text-emerald-600 font-medium mt-1">{p.gain}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Team Lineup Report — only renders for team sports (football, basketball) */}
          {teamLineupSupported && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4 text-blue-600" /> Team Lineup Report</CardTitle></CardHeader>
              <CardContent>
                {(() => {
                  const jc = selectedMatch.jersey_color || 'blue';
                  const jn = selectedMatch.jersey_number || '10';
                  const colorMap = { blue: 'bg-blue-500', red: 'bg-red-500', white: 'bg-white border text-slate-900', yellow: 'bg-yellow-400 text-slate-900', green: 'bg-emerald-500', black: 'bg-slate-900', orange: 'bg-orange-500', purple: 'bg-purple-500' };
                  const oppColor = jc === 'red' ? 'bg-blue-500' : 'bg-red-500';
                  // Roster size matches the sport (5 for basketball, 11 for football, default 11)
                  const rosterSize = sportConfig.key === 'basketball' ? 5 : 11;
                  const positionsRoster = positionsList.slice(0, rosterSize);
                  const teamPlayers = [
                    { num: jn, pos: selectedMatch.position || positionsList[0], name: profile?.name || 'You', rating: analysis.rating, motm: true, team: 'home' },
                    ...positionsRoster.slice(1).map((pos, i) => ({
                      num: String(i + 2), pos,
                      name: pos,
                      rating: (6 + Math.random() * 2).toFixed(1),
                      team: 'home',
                    })),
                  ].sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));

                  return (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`h-4 w-4 rounded-full ${colorMap[jc] || 'bg-blue-500'}`} />
                        <span className="text-xs font-semibold">Your Team</span>
                        <span className="text-xs text-muted-foreground mx-2">vs</span>
                        <div className={`h-4 w-4 rounded-full ${oppColor}`} />
                        <span className="text-xs font-semibold">{selectedMatch.opponent}</span>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-1.5">
                        {teamPlayers.filter(p => p.team === 'home').map((p, i) => (
                          <div key={i} className={`flex items-center gap-2 p-2 rounded-lg ${p.motm ? 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200' : 'bg-muted/30'}`}>
                            <div className={`h-8 w-8 rounded-full ${colorMap[jc] || 'bg-blue-500'} text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0`}>{p.num}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <p className="text-xs font-semibold truncate">{p.name}</p>
                                {p.motm && <Badge className="text-[8px] h-3.5 bg-amber-200 text-amber-800 border-0">MOTM</Badge>}
                              </div>
                              <p className="text-[10px] text-muted-foreground">{p.pos}</p>
                            </div>
                            <p className={`text-sm font-bold tabular-nums ${parseFloat(p.rating) >= 8 ? 'text-emerald-600' : parseFloat(p.rating) >= 7 ? 'text-blue-600' : 'text-amber-600'}`}>{p.rating}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Match Notes */}
          {selectedMatch.notes && (
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Match Notes</p>
                <p className="text-sm">{selectedMatch.notes}</p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Log Match Dialog — primary/secondary labels and position options adapt */}
      <Dialog open={showLogMatch} onOpenChange={setShowLogMatch}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Trophy className="h-5 w-5 text-emerald-600" /> Log {sportConfig.display_name} Match</DialogTitle></DialogHeader>
          <form onSubmit={handleLogMatch} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Opponent *</Label><Input value={matchForm.opponent} onChange={e => setMatchForm({...matchForm, opponent: e.target.value})} placeholder="Local Rivals" required /></div>
              <div className="space-y-1.5"><Label className="text-xs">Result</Label>
                <Select value={matchForm.result} onValueChange={v => setMatchForm({...matchForm, result: v})}><SelectTrigger><SelectValue placeholder="Result" /></SelectTrigger><SelectContent><SelectItem value="Won">Won</SelectItem><SelectItem value="Drew">Drew</SelectItem><SelectItem value="Lost">Lost</SelectItem></SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">{primaryLabel}</Label><Input type="number" value={matchForm.goals} onChange={e => setMatchForm({...matchForm, goals: e.target.value})} data-testid="form-primary-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">{secondaryLabel}</Label><Input type="number" value={matchForm.assists} onChange={e => setMatchForm({...matchForm, assists: e.target.value})} data-testid="form-secondary-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Minutes</Label><Input type="number" value={matchForm.minutes_played} onChange={e => setMatchForm({...matchForm, minutes_played: e.target.value})} /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Position / Role</Label>
              <Select value={matchForm.position} onValueChange={v => setMatchForm({...matchForm, position: v})}>
                <SelectTrigger data-testid="form-position-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {positionsList.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Jersey Selector — only for team-lineup sports */}
            {teamLineupSupported && (
            <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Jersey Details</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Jersey Color</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { name: 'blue', bg: 'bg-blue-500', ring: 'ring-blue-400' },
                      { name: 'red', bg: 'bg-red-500', ring: 'ring-red-400' },
                      { name: 'white', bg: 'bg-white border border-slate-300', ring: 'ring-slate-400' },
                      { name: 'yellow', bg: 'bg-yellow-400', ring: 'ring-yellow-400' },
                      { name: 'green', bg: 'bg-emerald-500', ring: 'ring-emerald-400' },
                      { name: 'black', bg: 'bg-slate-900', ring: 'ring-slate-600' },
                      { name: 'orange', bg: 'bg-orange-500', ring: 'ring-orange-400' },
                      { name: 'purple', bg: 'bg-purple-500', ring: 'ring-purple-400' },
                    ].map(c => (
                      <button key={c.name} type="button" onClick={() => setMatchForm({...matchForm, jersey_color: c.name})}
                        className={`h-7 w-7 rounded-full ${c.bg} ${matchForm.jersey_color === c.name ? `ring-2 ${c.ring} ring-offset-2` : ''} transition-all hover:scale-110`}
                        title={c.name} />
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Jersey Number</Label>
                  <div className="flex flex-wrap gap-1">
                    {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15].map(n => (
                      <button key={n} type="button" onClick={() => setMatchForm({...matchForm, jersey_number: String(n)})}
                        className={`h-7 w-7 rounded-lg text-xs font-bold transition-all ${matchForm.jersey_number === String(n) ? 'bg-primary text-primary-foreground ring-2 ring-primary/30' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            )}
            <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea value={matchForm.notes} onChange={e => setMatchForm({...matchForm, notes: e.target.value})} placeholder="Key moments, performance notes..." rows={2} /></div>
            <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={analyzing} data-testid="form-submit">
              {analyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
              {analyzing ? 'Analyzing...' : 'Log & Analyze'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
